// tslint:disable: cyclomatic-complexity
import Styled, { css } from 'styled-components';

import { StyledText } from '../../../Typography/Text/StyledText';
import { cssDeclaration, VALID_BORDER_DATA } from '../../../Utils';
import { IFocusControl, IInputError } from '../../common';
import { sharedRadioInputStyles } from '../common';

interface IRadioInputStyleProps extends IInputError, React.InputHTMLAttributes<HTMLInputElement> {
  multiLine?: boolean;
}

const checkedStyleSolid = css`
  /*
    @ts-ignore required because checkedBgColor only exists on the theme interface
    if checkedStyle === solid
    see: src/components/_ThemeTwo_/Theme/common.ts:44
  */
    ${({ theme: { themeTwo }}) =>
    // @ts-ignore
    themeTwo.form.radio.button.checkedBgColor && css`
    /* background-color */
    ${cssDeclaration(
      themeTwo,
      'background-color',
      // @ts-ignore
      themeTwo.form.radio.button.checkedBgColor
    )}
  `}
`;

const checkedStyleRing = css`
  border: none;
`;

const labelAndIndicatorStyles = css`
  ${({ theme: { themeTwo }}) => css`
      /* outline */ ${cssDeclaration(themeTwo, 'outline', themeTwo.global.outline)}
  `}
`;

const indicatorOnlyStyles = css`
  ${({ theme }) => {
    const themeTwo = theme.themeTwo;
    const { form: { radio }} = themeTwo;
    const size = themeTwo.global.edgeSize[radio.button.size] || radio.button.size;
    const offSet = '6px';

    return css`
      &::after {
        display: inline-block;
        content: "";
        cursor: pointer;
        position: absolute;
        border-radius: 50%;
        margin-bottom: 0;
        padding: 0.25em;

        top: calc((-${offSet} / 2));
        left: calc((-${offSet} / 2));
        width: calc(${size} + ${offSet});
        height: calc(${size} + ${offSet});
        /* border */${cssDeclaration(themeTwo, 'border', themeTwo.global.outline as VALID_BORDER_DATA)};
      }
    `;
  }}
`;

export const RadioLabel = Styled.label<IInputError>`
  position: relative;
  cursor: pointer;
  width: fit-content;

  ${({ theme, error }) => {
    const themeTwo = theme.themeTwo;
    const { form: { radio }} = themeTwo;
    const {
      button: {
        state: { error: errorState },
      },
    } = radio;

    const isAmount = theme.name === 'amount' || theme.name === 'amountDark';
    const size = themeTwo.global.edgeSize[radio.button.size] || radio.button.size;

    return css`
      // need to set height to allow focus outline to wrap around component without cutting off the radio button
      /* padding */ ${radio.button.label && cssDeclaration(themeTwo, 'padding', radio.button.label.padding)}

      ${radio.button.label && css`
        /* color */ ${radio.button.label.font.color && cssDeclaration(themeTwo, 'color', radio.button.label.font.color)};
        /* font-size */ ${radio.button.label.font.size && cssDeclaration(themeTwo, 'font-size', radio.button.label.font.size)};
      `}

      &::before {
        margin-bottom: 0;
        padding: 0.25em;
        display: inline-block;
        content: "";
        cursor: pointer;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 0;
        border-radius: 50%;
        width: ${size};
        height: ${size};
        /* background-color */ ${cssDeclaration(themeTwo, 'background-color', radio.button.backgroundColor)};
        /* border */ ${radio.button.border && cssDeclaration(themeTwo, 'border', radio.button.border)};
      }

      // error
      ${error && errorState.label && errorState.label.font.color && css`
        /* color */ ${cssDeclaration(themeTwo, 'color', errorState.label.font.color)};
      `}

      span:not(${StyledText}) {
        display: inline-block;
        margin-left: 2em;
        vertical-align: middle;
        /* margin */ ${radio.button.label && cssDeclaration(themeTwo, 'margin', radio.button.label.margin)};

        ${isAmount && css`
          transform: translateY(2px);
        `}
      }
    `;
  }}
`;

export const RadioInput = Styled.input<IRadioInputStyleProps & IFocusControl>`
  ${sharedRadioInputStyles};
  appearance: none !important;

  ${({ theme, error, multiLine, clickFocus }) => {
    // tslint:disable-next-line:max-func-body-length
    const themeTwo = theme.themeTwo;
    const { form: { radio }} = themeTwo;
    const { button: {
        state: { checked, checkedHover, disabled, disabledChecked, focus, hover, error: errorState },
    }} = radio;

    return css`
      & + ${RadioLabel} {
        line-height: ${radio.button.label && radio.button.label.font.lineHeight};
        /* color */
        ${radio.button.label && radio.button.label.font.color &&
          cssDeclaration(themeTwo, 'color', radio.button.label.font.color)
        };
      }

      &:checked + ${RadioLabel} {
        /* color */ ${checked.label && checked.label.font.color && cssDeclaration(themeTwo, 'color', checked.label.font.color)};
        &::before {
          /* box-shadow */ ${checked.shadow && cssDeclaration(themeTwo, 'box-shadow', checked.shadow)};
          /* border */ ${checked.border && cssDeclaration(themeTwo, 'border', checked.border)};

          // sets correct styles for checked variants
          ${radio.button.checkedStyle === 'ring' ? checkedStyleRing : checkedStyleSolid}
        }
      }

      &:checked:hover + ${RadioLabel}::before {
        ${checkedHover && css`
          /* border */ ${checkedHover.border && cssDeclaration(themeTwo, 'border', checkedHover.border)};
          ${checkedHover.backgroundColor &&
            /* background-color */ cssDeclaration(themeTwo, 'background-color', checkedHover.backgroundColor)
          };
          ${checkedHover.shadow && css`
            /* box-shadow */ ${cssDeclaration(themeTwo, 'box-shadow', checkedHover.shadow)};
          `}
        `}
      }

      &:disabled + ${RadioLabel} {
        cursor: not-allowed;
         /* color */ ${disabled.label && disabled.label.font.color && cssDeclaration(themeTwo, 'color', disabled.label.font.color)};

        &::before {
          cursor: not-allowed;
          /* background-color */ ${disabled.backgroundColor && cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)};
          /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
        }
      }

      &:disabled:checked + ${RadioLabel}::before,
      &:disabled:checked:hover + ${RadioLabel}::before {
        cursor: not-allowed;
        /* border */ ${disabledChecked.border && cssDeclaration(themeTwo, 'border', disabledChecked.border)};
        /* box-shadow */ ${disabledChecked.shadow && cssDeclaration(themeTwo, 'box-shadow', disabledChecked.shadow)};
        /* background-color */
        ${disabledChecked.backgroundColor &&
          cssDeclaration(themeTwo, 'background-color', disabledChecked.backgroundColor)
        };
      }

      &:disabled:hover + ${RadioLabel}::before {
        cursor: not-allowed;
        /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
      }

      ${!clickFocus && css`
        &:focus + ${RadioLabel} {
          ${focus.focusPosition === 'indicatorOnly' ? indicatorOnlyStyles : labelAndIndicatorStyles};
        }
      `}

      &:hover + ${RadioLabel}::before {
        /* border */ ${hover.border && cssDeclaration(themeTwo, 'border', hover.border)};
      }

      // error
      ${error && css`
        & + ${RadioLabel} {
          /* color */ ${errorState.label && errorState.label.font.color && cssDeclaration(themeTwo, 'color', errorState.label.font.color)};
        }
        & + ${RadioLabel}::before {
          /* background-color */ ${errorState.backgroundColor && cssDeclaration(themeTwo, 'background-color', errorState.backgroundColor)};
          /* border */ ${errorState.border && cssDeclaration(themeTwo, 'border', errorState.border)};
        }
      `}

      // multiline
      ${multiLine && css`
        & + ${RadioLabel} {
          display: flex;
          height: auto;

          // resets styles if placed by the theme
          padding: 0;
          & span {
            margin-top: 0;
          }

          &::before {
            top: 0;
            transform: none;
          }
        }
      `}
    `;
  }}
`;
